import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  Breadcrumbs,
  Link as MUILink,
  InputBase,
  Badge,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Campaign as CampaignIcon,
  Create as CreateIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Store as StoreIcon,
  CalendarToday as CalendarIcon,
  AccountCircle,
  Settings,
  Logout,
  NotificationsNone as NotificationsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { brandColors } from '../../theme';
import DynamicSquares from '../BrandElements/DynamicSquares';
import Footer from '../Footer/Footer';

const drawerWidth = 260;

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [domainSelection, setDomainSelection] = useState<'marketing' | 'werbemittel'>('marketing');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('all');
  const [customerMenuAnchor, setCustomerMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const marketingItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Campaign Management', icon: <CampaignIcon />, path: '/campaigns' },
    { text: 'Content Creation', icon: <CreateIcon />, path: '/content' },
    { text: 'Calendar Planning', icon: <CalendarIcon />, path: '/calendar' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
    { text: 'User Management', icon: <PeopleIcon />, path: '/users' },
  ];
  const werbemittelItems = [
    { text: 'Landing Page', icon: <CampaignIcon />, path: '/werbemittel/landing' },
    { text: 'Artikel', icon: <CreateIcon />, path: '/werbemittel/artikel' },
    { text: 'Media Services', icon: <StoreIcon />, path: '/media-services' },
  ];
  const reportingItems = [
    { text: 'Report Editor', icon: <AnalyticsIcon />, path: '/reports' },
  ];
  const displayedNavItems = domainSelection === 'marketing' ? marketingItems : werbemittelItems;
  const currentLabel = (() => {
    const allItems = [...marketingItems, ...werbemittelItems];
    const item = allItems.find(item => item.path === location.pathname);
    if (item) return item.text;
    if (location.pathname === '/' || location.pathname === '/dashboard') return 'Dashboard';
    return 'R2B Platform';
  })();

  useEffect(() => {
    const werbemittelPaths = werbemittelItems.map((i) => i.path);
    const reportingPaths = [{ text: 'Report Editor', icon: null, path: '/reports' }].map((i) => i.path);
    const isWerbemittelRoute = werbemittelPaths.includes(location.pathname);
    const isReportingRoute = reportingPaths.includes(location.pathname);
    if (isReportingRoute) return; // allow reporting regardless of current domain
    if (domainSelection === 'werbemittel' && !isWerbemittelRoute) {
      navigate(werbemittelItems[0].path);
    }
    if (domainSelection === 'marketing' && isWerbemittelRoute) {
      navigate('/dashboard');
    }
  }, [domainSelection, location.pathname, navigate]);

  useEffect(() => {
    const werbemittelPaths = werbemittelItems.map((i) => i.path);
    const reportingPaths = [{ text: 'Report Editor', icon: null, path: '/reports' }].map((i) => i.path);
    const isWerbemittelRoute = werbemittelPaths.includes(location.pathname);
    const isReportingRoute = reportingPaths.includes(location.pathname);
    if (isReportingRoute) return; // keep current toggle while in reporting
    setDomainSelection(isWerbemittelRoute ? 'werbemittel' : 'marketing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const customers = ['Kunde A', 'Kunde B', 'Kunde C'];
  const handleOpenCustomerMenu = (e: React.MouseEvent<HTMLElement>) => setCustomerMenuAnchor(e.currentTarget);
  const handleCloseCustomerMenu = () => setCustomerMenuAnchor(null);
  const handleSelectCustomer = (name: string) => {
    setSelectedCustomer(name);
    handleCloseCustomerMenu();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const drawer = (
    <Box sx={{ height: '100%', backgroundColor: 'var(--slate-50)', borderRight: '1px solid var(--line)' }}>
      <Toolbar sx={{
        minHeight: '64px !important',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        p: 0,
        borderBottom: '1px solid var(--line)'
      }}>
        <Box
          component="img"
          src="/logo512.png"
          alt="R2B Logo"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
      </Toolbar>
      <List sx={{ pt: 2, px: 1 }}>
        {displayedNavItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ px: 0.5, mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 999,
                color: 'var(--ink)',
                '&.Mui-selected': {
                  backgroundColor: 'var(--slate-100)',
                  color: 'var(--ink)'
                },
                '&:hover': {
                  backgroundColor: 'var(--slate-100)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'var(--muted)', minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  fontSize: '0.9rem',
                  color: 'var(--ink)'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ px: 2, py: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'var(--muted)', letterSpacing: 0.6 }}>Reporting</Typography>
      </Box>
      <List sx={{ pt: 0, px: 1 }}>
        {reportingItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ px: 0.5, mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 999,
                color: 'var(--ink)',
                '&.Mui-selected': {
                  backgroundColor: 'var(--slate-100)',
                  color: 'var(--ink)'
                },
                '&:hover': {
                  backgroundColor: 'var(--slate-100)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'var(--muted)', minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  fontSize: '0.9rem',
                  color: 'var(--ink)'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ color: 'var(--muted)' }}>
              <MUILink underline="hover" color="inherit" onClick={() => { setDomainSelection('marketing'); navigate('/dashboard'); }} sx={{ cursor: 'pointer' }}>
                Home
              </MUILink>
              <Typography color="var(--ink)">{currentLabel}</Typography>
            </Breadcrumbs>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, ml: 1 }}>
              {/* Marketing | Werbemittel toggle */}
              <Box sx={{ display: 'flex', border: '1px solid var(--line)', borderRadius: '999px', backgroundColor: 'var(--card)', p: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setDomainSelection('marketing')}
                  sx={{
                    height: 30,
                    width: 'auto',
                    px: 1.5,
                    borderRadius: '999px',
                    color: 'var(--ink)',
                    backgroundColor: domainSelection === 'marketing' ? 'var(--slate-100)' : 'transparent'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Marketing</Typography>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setDomainSelection('werbemittel')}
                  sx={{
                    height: 30,
                    width: 'auto',
                    px: 1.5,
                    borderRadius: '999px',
                    color: 'var(--ink)',
                    backgroundColor: domainSelection === 'werbemittel' ? 'var(--slate-100)' : 'transparent'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Werbemittel</Typography>
                </IconButton>
              </Box>
              {/* Customer selector with All button */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  pl: 1,
                  pr: 0.5,
                  height: 40,
                  border: '1px solid var(--line)',
                  borderRadius: '999px',
                  backgroundColor: 'var(--card)'
                }}>
                  <Typography variant="caption" sx={{ color: 'var(--muted)' }}>Kunde</Typography>
                  <Button
                    size="small"
                    onClick={handleOpenCustomerMenu}
                    sx={{
                      textTransform: 'none',
                      fontSize: 14,
                      px: 1.25,
                      borderRadius: '999px',
                      color: 'var(--ink)'
                    }}
                  >
                    {selectedCustomer === 'all' ? 'Alle' : selectedCustomer}
                  </Button>
                  <Menu
                    anchorEl={customerMenuAnchor}
                    open={Boolean(customerMenuAnchor)}
                    onClose={handleCloseCustomerMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <MenuItem onClick={() => handleSelectCustomer('all')}>Alle</MenuItem>
                    {customers.map((c) => (
                      <MenuItem key={c} onClick={() => handleSelectCustomer(c)}>{c}</MenuItem>
                    ))}
                  </Menu>
                </Box>
                <IconButton size="small" onClick={() => setSelectedCustomer('all')} sx={{ height: 32, width: 'auto', px: 1.25, border: '1px solid var(--line)', borderRadius: '999px', backgroundColor: 'var(--card)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Alle</Typography>
                </IconButton>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              height: 40,
              width: 280,
              border: '1px solid var(--line)',
              borderRadius: '999px',
              backgroundColor: 'var(--card)'
            }}>
              <SearchIcon sx={{ color: 'var(--muted)', fontSize: 18 }} />
              <InputBase placeholder="Suchen" sx={{ fontSize: 14, flex: 1 }} />
            </Box>
            <Tooltip title="Benachrichtigungen">
              <IconButton sx={{ height: 40, width: 40, p: 1 }}>
                <Badge color="primary" variant="dot">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            {/* logo moved to left drawer header */}
            <IconButton
              size="small"
              edge="end"
              aria-label="account of current user"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              sx={{ height: 40, width: 40, p: 0 }}
            >
              <Avatar sx={{ bgcolor: 'var(--slate-200)', color: 'var(--ink)', width: 32, height: 32 }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem onClick={handleProfileMenuClose}>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={handleProfileMenuClose}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleProfileMenuClose}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'var(--bg)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, display: 'flex' }}>
          <Box
            sx={{
              maxWidth: 1360,
              mx: 'auto',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Outlet />
            </Box>
            <Footer />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
